import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BigButton } from '../components/BigButton';
import { useExamMode } from '../context/ExamModeContext';

// Pre-attempt screen. Captures candidate / paper details and starts the locked
// exam session. Once "Start" is tapped the candidate is locked into the flow
// until they (or the invigilator) ends the attempt explicitly.
export function ExamModeIntroScreen() {
  const navigate = useNavigate();
  const { active, start } = useExamMode();
  const [candidateName, setCandidateName] = useState('');
  const [candidateNumber, setCandidateNumber] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [paperTitle, setPaperTitle] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleStart() {
    setError(null);
    if (!consent) {
      setError('Please tick the consent checkbox to confirm the candidate understands.');
      return;
    }
    if (!paperTitle.trim()) {
      setError('Please enter the paper title (e.g. "GCSE Geography Paper 1").');
      return;
    }
    setBusy(true);
    try {
      await start({
        candidateName: candidateName.trim(),
        candidateNumber: candidateNumber.trim(),
        schoolName: schoolName.trim(),
        paperTitle: paperTitle.trim(),
      });
      navigate('/app/capture');
    } catch (e) {
      setError((e as Error).message || 'Could not start the exam session.');
      setBusy(false);
    }
  }

  if (active) {
    return (
      <AppShell title="Exam Mode">
        <div className="px-5 py-6 max-w-md mx-auto">
          <p className="text-ink/70">
            An exam attempt is already underway. Continue with the active attempt or end it from
            the banner above.
          </p>
          <div className="mt-4 flex flex-col gap-3">
            <BigButton onClick={() => navigate('/app/capture')}>Continue attempt</BigButton>
            <BigButton variant="secondary" onClick={() => navigate('/app/exam/end')}>
              End attempt
            </BigButton>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Exam Mode">
      <div className="px-5 py-6 max-w-md mx-auto">
        <header>
          <p className="uppercase tracking-[0.18em] text-xs text-red-700 font-semibold">
            Locked attempt
          </p>
          <h1 className="text-2xl font-semibold tracking-tight mt-2">Start an exam attempt</h1>
          <p className="text-sm text-ink/65 leading-relaxed mt-2">
            Exam Mode is the locked version of Answer Assist for use as an access arrangement
            in real exams. All AI helpers are switched off. Every action is recorded for the
            audit log.
          </p>
        </header>

        <section className="mt-6 bg-white border border-ink/10 rounded-2xl p-5 space-y-4">
          <Field
            label="Paper title"
            placeholder="e.g. GCSE Geography Paper 1"
            value={paperTitle}
            onChange={setPaperTitle}
            required
          />
          <Field
            label="Candidate name"
            placeholder="As it appears on the candidate's exam roster"
            value={candidateName}
            onChange={setCandidateName}
          />
          <Field
            label="Candidate number"
            placeholder="School-issued number"
            value={candidateNumber}
            onChange={setCandidateNumber}
          />
          <Field
            label="Centre / school"
            placeholder="School name"
            value={schoolName}
            onChange={setSchoolName}
          />
        </section>

        <section className="mt-5 bg-warm/60 border border-amber-300 rounded-2xl p-5 text-sm leading-relaxed">
          <p className="font-semibold text-ink">During this attempt:</p>
          <ul className="mt-2 space-y-1.5 text-ink/80 list-disc list-inside">
            <li>AI question detection is OFF (tap to add each answer slot yourself).</li>
            <li>The "Simpler" button is OFF.</li>
            <li>Word prediction is OFF.</li>
            <li>Auto-correction of dictated speech is OFF.</li>
            <li>Premium voices are OFF (basic Read Aloud only).</li>
            <li>Settings are locked.</li>
            <li>Every action is timestamped in an append-only audit log.</li>
          </ul>
          <p className="mt-3 text-ink/70 text-xs">
            See the <Link to="/exam-mode" className="text-accent underline">Exam Mode policy</Link> for the full JCQ alignment.
          </p>
        </section>

        <label className="mt-5 flex items-start gap-3 text-sm text-ink/80 leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-accent"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span>
            I confirm the candidate understands they will be locked into this attempt and that
            all AI helpers are switched off for the duration of the paper.
          </span>
        </label>

        {error && (
          <div role="alert" className="mt-4 bg-red-50 border border-red-300 text-red-900 rounded-xl p-3 text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3">
          <BigButton onClick={handleStart} disabled={busy}>
            {busy ? 'Starting...' : 'Start exam attempt'}
          </BigButton>
          <BigButton variant="secondary" onClick={() => navigate('/app')}>
            Cancel
          </BigButton>
        </div>
      </div>
    </AppShell>
  );
}

function Field({
  label,
  placeholder,
  value,
  onChange,
  required,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-ink/55 font-medium">
        {label} {required && <span className="text-red-600">*</span>}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2.5 rounded-xl border border-ink/15 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
      />
    </label>
  );
}
